import { theme as defaultTheme, Spinner } from "@mindlercare/ui-web";
import { IcbtResponse } from "api/types";
import { useGetIcbtResponses } from "api/useIcbtQueries";
import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { Timestamp } from "./Timestamp";
import { isErrorWithStatus } from "api/helpers";
import { LEGACY_NOTE_ID } from "../../constants";

interface NotesProps {
  userId: number;
  icbtRequestId: string;
  legacyNotePageTitle?: string;
}
const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  border-radius: 8px;
`;

const NoteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 10px;
`;

const NoteQuestion = styled(Typography)`
  font-weight: ${() => defaultTheme.FONT.WEIGHT.MEDIUM};
  font-size: ${() => defaultTheme.FONT.SIZE.LARGE};
`;

const NoteAnswer = styled(Typography)`
  background-color: ${() => defaultTheme.COLORS.GRAY_LIGHT};
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap;
`;

export const EmptyNotes: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <NotesContainer>
      <NoteAnswer variant="body" insensitive>
        {message || t("NO_NOTES_LEGACY")}
      </NoteAnswer>
    </NotesContainer>
  );
};

export const Notes: React.FC<NotesProps> = ({
  userId,
  icbtRequestId,
  legacyNotePageTitle,
}) => {
  const { t } = useTranslation();
  const {
    data: notes,
    isLoading,
    error,
  } = useGetIcbtResponses({
    userId,
    icbtRequestId,
  });

  const defaultQuestion = t("ICBT_RESPONSE_DEFAULT_QUESTION");
  // Only the legacy reflection note is shown here. Other (Contentful-placed)
  // notes are rendered inline in the program page under their own label.
  const legacyNotes = notes?.filter(
    (note) => !note.note_id || note.note_id === LEGACY_NOTE_ID,
  );
  if (isLoading) return <Spinner />;
  if (error && isErrorWithStatus(error, 403)) {
    return (
      <NotesContainer>
        <Typography variant="h3">
          {t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")}
        </Typography>
      </NotesContainer>
    );
  }
  if (!legacyNotes || legacyNotes.length === 0) return <EmptyNotes />;
  return (
    <NotesContainer>
      {legacyNotes.map((note: IcbtResponse) => (
        <NoteWrapper key={note.response_id}>
          <NoteQuestion variant="body">
            {legacyNotePageTitle ?? note.question ?? defaultQuestion}
          </NoteQuestion>
          <NoteAnswer variant="body">{note.response_text}</NoteAnswer>
          <Timestamp
            status="edited"
            showAccountSymbol={true}
            alignSelf="flex-end"
          >
            {note.updated_at}
          </Timestamp>
        </NoteWrapper>
      ))}
    </NotesContainer>
  );
};
